/**
 * Policy-led fee derivation for the Create RMA page (FR7b).
 *
 * One instance per scope: the session holds one over the union of all drafts' items; each draft
 * holds one over its own items in policy-led mode. Unlimited mode uses the session instance only.
 */

import { makeAutoObservable } from 'mobx';
import {
	AMOUNT_TYPE,
	FEE_TYPE,
	FEE_TYPE_INCLUDS_COST,
	IPolicyFee,
	SHIPPING_LABEL_FEE_TYPES,
} from '../../policies/policy-types';
import CreateRmaSettingsStore from './create-rma-settings-store';
import RequestProductStore from './request-product-store';
import RmaFeesListStore from './rma-fees-list-store';

export interface IPolicyFeeDerivationScope {
	/** the scope's label-cost capability: intersected across drafts, or one draft's own (FR7c) */
	isLabelCostSupported: boolean;
	/** any item in scope carrying a waive-all-fees rule (FR7b) */
	shouldWaveAllFees: boolean;
}

class CreateRmaPolicyFeeDerivationStore {
	private _feesListStore: RmaFeesListStore;

	/** Builds an empty fee configuration from the page settings defaults, ready to be derived. */
	constructor(private _settingsStore: CreateRmaSettingsStore) {
		makeAutoObservable(this, {}, { autoBind: true });

		const { return_fee, label_fee } = this._settingsStore.createRmaFeeDefaults;
		this._feesListStore = new RmaFeesListStore(return_fee, label_fee);
	}

	/** The derived return/label fee configuration this scope last wrote. */
	get feesListStore(): RmaFeesListStore {
		return this._feesListStore;
	}

	/**
	 * Entry point: recomputes both fees from the items in scope.
	 * Resets to uncharged when the scope is empty or any item waives all fees; otherwise delegates
	 * to the return-fee and label-fee max-wins rules independently.
	 */
	public deriveFromItems(
		items: RequestProductStore[],
		scope: IPolicyFeeDerivationScope,
	): void {
		if (scope.shouldWaveAllFees || items.length === 0) {
			this._feesListStore.labelFee.resetStore();
			this._feesListStore.returnFee.resetStore();
			return;
		}

		this._setReturnFeeStoreData(items);
		this._setLabelFeeStoreData(items, scope.isLabelCostSupported);
	}

	/** Looks up the return-fee row on the policy rule that owns the given item. */
	private _getReturnFeeSettings(policyId: number | undefined): IPolicyFee | undefined {
		if (!policyId) {
			return undefined;
		}
		return this._settingsStore.findPolicyFeesSettings(policyId).find((fee) => fee.fee_type === FEE_TYPE.RETURN_FEE);
	}

	/** Looks up the shipping-label fee row on the policy rule that owns the given item. */
	private _getLabelFeeSettings(policyId: number | undefined): IPolicyFee | undefined {
		if (!policyId) {
			return undefined;
		}
		return this._settingsStore.findPolicyFeesSettings(policyId).find((fee) =>
			this._isShippingLabelFeeType(fee.fee_type),
		);
	}

	/** null fee_type is the legacy "fixed label fee" row, same as policy-store and request-product-store. */
	private _isShippingLabelFeeType(feeType: FEE_TYPE | null | undefined): boolean {
		return feeType == null || SHIPPING_LABEL_FEE_TYPES.includes(feeType as FEE_TYPE);
	}

	/**
	 * Picks the return fee by max calculated amount across items (per unit — quantity ignored).
	 * Fixed amounts use the server-converted value; percentages keep the raw rate from the policy.
	 */
	private _setReturnFeeStoreData(items: RequestProductStore[]): void {
		this._feesListStore.returnFee.resetStore();

		const productWithMaxCalculatedFeeAmount = items.reduce((acc, product) => {
			return acc.returnFeeCalculatedAmount > product.returnFeeCalculatedAmount ? acc : product;
		}, items[0]);

		const returnFeeSettings = this._getReturnFeeSettings(productWithMaxCalculatedFeeAmount.policyId);
		if (!returnFeeSettings) {
			return;
		}

		let returnFeeAmount = returnFeeSettings.amount;
		if (returnFeeSettings.amount_type === AMOUNT_TYPE.FIXED) {
			returnFeeAmount = productWithMaxCalculatedFeeAmount.returnFeeCalculatedAmount;
		}

		this._feesListStore.returnFee.setAmount(returnFeeAmount!);
		this._feesListStore.returnFee.setAmountType(returnFeeSettings.amount_type);
		this._feesListStore.returnFee.setCharged(returnFeeSettings.is_charged);
		this._feesListStore.returnFee.setFeeType(returnFeeSettings.fee_type);
		this._feesListStore.returnFee.setIsChargeTax(returnFeeSettings.charge_tax ?? false);
	}

	/**
	 * Picks the label fee in two stages: prefer cost-including types when the scope supports
	 * carrier cost, otherwise consider fixed shipment fees only; then take the highest per-unit
	 * calculated amount among the candidates.
	 */
	private _setLabelFeeStoreData(items: RequestProductStore[], isLabelCostSupported: boolean): void {
		this._feesListStore.labelFee.resetStore();

		const chargedLabelFeeProducts = items.filter(
			(product) => this._getLabelFeeSettings(product.policyId)?.is_charged,
		);

		if (chargedLabelFeeProducts.length === 0) {
			return;
		}

		const costIncludingProducts = chargedLabelFeeProducts.filter((product) => {
			const feeType = this._getLabelFeeSettings(product.policyId)?.fee_type as FEE_TYPE | undefined;
			return feeType != null && FEE_TYPE_INCLUDS_COST.includes(feeType);
		});

		const candidates = isLabelCostSupported && costIncludingProducts.length > 0
			? costIncludingProducts
			: chargedLabelFeeProducts.filter((product) => {
				const feeType = this._getLabelFeeSettings(product.policyId)?.fee_type as FEE_TYPE | undefined;
				return feeType == null || !FEE_TYPE_INCLUDS_COST.includes(feeType);
			});

		if (candidates.length === 0) {
			return;
		}

		const winningProduct = candidates.reduce(
			(acc, product) => acc.labelFeeCalculatedAmount > product.labelFeeCalculatedAmount ? acc : product,
			candidates[0],
		);

		this._applyLabelFeeFromProduct(winningProduct);
	}

	/**
	 * Writes the winning item's label-fee policy onto the store.
	 * COST leaves amount unset (filled at display time from the carrier rate); fixed non-COST types
	 * use the server-converted per-unit amount.
	 */
	private _applyLabelFeeFromProduct(product: RequestProductStore): void {
		const labelFeeSettings = this._getLabelFeeSettings(product.policyId);
		if (!labelFeeSettings?.is_charged) {
			return;
		}

		const feeType = labelFeeSettings.fee_type ?? FEE_TYPE.SHIPMENT_FEE;

		let amount = labelFeeSettings.amount;
		if (feeType !== FEE_TYPE.COST && labelFeeSettings.amount_type === AMOUNT_TYPE.FIXED) {
			amount = product.labelFeeCalculatedAmount;
		}

		if (feeType !== FEE_TYPE.COST) {
			this._feesListStore.labelFee.setAmount(amount!);
			this._feesListStore.labelFee.setAmountType(labelFeeSettings.amount_type);
		}

		this._feesListStore.labelFee.setCharged(labelFeeSettings.is_charged);
		this._feesListStore.labelFee.setFeeType(feeType);
		this._feesListStore.labelFee.setIsChargeTax(labelFeeSettings.charge_tax ?? false);
	}
}

export default CreateRmaPolicyFeeDerivationStore;
